import { useState } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import { GifIcon, PdfIcon, PlayFilledIcon, Text } from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { getProxiedThumbnailUrl } from '~publish/legacy/utils/bufferImages'

import { getAltText } from './helpers'
import { MediaMarker } from '../../MediaMarker/MediaMarker'

import styles from './PostMediaAsset.module.css'

export const PostMediaAsset_Asset = graphql(`
  fragment PostMediaAsset_Asset on Asset {
    __typename
    id
    mimeType
    thumbnail
    source
    ... on ImageAsset {
      image {
        altText
        width
        height
        isAnimated
      }
    }
    ... on VideoAsset {
      video {
        width
        height
        isVideoProcessing
      }
    }
    ... on DocumentAsset {
      document {
        filesize
        numPages
      }
    }
  }
`)

export type PostMediaAsset_AssetData = ResultOf<typeof PostMediaAsset_Asset>

type PostCardMediaProps = {
  asset: FragmentOf<typeof PostMediaAsset_Asset>
  className?: string
}

const getThumbnailSource = (asset: PostMediaAsset_AssetData): string => {
  const thumbnail = asset.thumbnail ?? asset.source

  if (!thumbnail) {
    return thumbnail
  }

  if (isOfType(asset, 'ImageAsset')) {
    return asset.image.isAnimated
      ? thumbnail
      : getProxiedThumbnailUrl(thumbnail)
  }

  if (isOfType(asset, 'VideoAsset')) {
    return getProxiedThumbnailUrl(thumbnail)
  }

  return thumbnail
}

const getIcon = (asset: PostMediaAsset_AssetData): JSX.Element | null => {
  if (isOfType(asset, 'VideoAsset')) {
    return <PlayFilledIcon />
  }

  if (isOfType(asset, 'ImageAsset') && asset.image.isAnimated) {
    return <GifIcon />
  }

  if (isOfType(asset, 'DocumentAsset')) {
    return <PdfIcon />
  }

  return null
}

export const PostMediaAsset = ({
  asset: passedAsset,
  className,
}: PostCardMediaProps): JSX.Element | null => {
  const asset = readFragment(PostMediaAsset_Asset, passedAsset)
  const [loaded, setLoaded] = useState(false)

  if (!asset) {
    return null
  }

  const icon = getIcon(asset)
  const altText = getAltText(asset)

  const thumbnail = getThumbnailSource(asset)
  const isVideoProcessing =
    isOfType(asset, 'VideoAsset') && asset.video.isVideoProcessing

  const emptyThumbnailMessage = isVideoProcessing
    ? 'Creating thumbnail'
    : 'No thumbnail'

  return (
    <div className={clsx(styles.asset, className)}>
      {thumbnail ? (
        <img
          src={thumbnail}
          alt={altText}
          className={clsx(styles.thumbnail, loaded && styles.loaded)}
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
      ) : (
        <div className={clsx(styles.thumbnail, styles.empty)}>
          <Text color="subtle" align="center">
            {emptyThumbnailMessage}
          </Text>
        </div>
      )}
      {icon && (
        <MediaMarker className={styles.badge} aria-hidden>
          {icon}
        </MediaMarker>
      )}
    </div>
  )
}
